import type { TFunction } from 'i18next';
import { AlertRow } from './AlertRow';
import { OverspeedRow } from './OverspeedRow';
import { AfterHoursRow } from './AfterHoursRow';
import { TrackerHealthRow } from './TrackerHealthRow';
import { PowerRow } from './PowerRow';
import { IgnitionRow } from './IgnitionRow';
import { TamperRow } from './TamperRow';
import { IdleRow } from './IdleRow';
import { DataDelayedRow } from './DataDelayedRow';
import { MovedWhileParkedRow } from './MovedWhileParkedRow';
import { GenericAlertRow, type GenericWireAlert } from './GenericAlertRow';
import { DriverUnlinkRow } from './DriverUnlinkRow';
import { MaintenanceRow } from '@/features/maintenance/MaintenanceRow';
import {
  isAfterHours,
  isGeofenceCrossing,
  isMaintenance,
  isOverspeed,
  isTrackerHealth,
  isPower,
  isIgnition,
  isTamper,
  isIdle,
  isDataDelayed,
  isMovedWhileParked,
  isDriverUnlink,
  type FeedItem,
} from './types';

/**
 * The feed's row DISPATCHER (slice 19). The alerts feed is a discriminated union of
 * TWELVE item shapes — a geofence crossing, an overspeed episode, a maintenance
 * reminder (slice 28), a tracker-health event (slice 13f), an after-hours use
 * (slice 37), a power event and an ignition event (slice 54), a tamper alert
 * (slice 55), an idle alert (slice 66k), a data-delayed alert (slice 76), a
 * moved-while-parked alert (slice 82a), and a driver-unlink-request alert
 * (slice D2) — and this is the single place the narrowing happens for the list.
 * Every `.map` over feed rows goes through here, so each new rider is added in ONE
 * spot, not at each call site.
 *
 * 🔴 Maintenance and tracker-health rows never deep-link to a map — neither has a
 * location. Overspeed and after-hours rows DO (both carry an observed fix). A
 * power_cut, a tamper, an ignition row (since slice 56b) and an idle row (slice 66k)
 * are the in-between case: each centres the map ONLY when it carries a last-known
 * fix (see the respective row), and is a plain Link otherwise.
 *
 * 🔴 EXHAUSTIVE BY CONSTRUCTION, FORWARD-COMPATIBLE AT RUNTIME (slice 83, §F).
 * After the twelve narrowings the residual type is `never`, so
 * `assertAllFeedItemsHandled(item)` typechecks today and a THIRTEENTH feed item would
 * make this file a COMPILE error until it is handled above — the compile-time
 * exhaustiveness the old `assertNever` gave is kept, because the parameter is typed
 * `never`. But unlike `assertNever` it does NOT throw: at RUNTIME an older deployed
 * build (or a client holding a cached service worker) can receive an alert `type`
 * this build's union does not include, and crashing the whole feed over one unknown
 * row is the wrong answer. Execution falls through to a generic, markable row.
 *
 * `onSelect` is typed over the union; each concrete row accepts a narrower callback
 * by contravariance, so the same handler flows without a cast.
 */
function assertAllFeedItemsHandled(_x: never): void {
  /* type-level exhaustiveness only — intentionally does nothing at runtime */
}

export function FeedRow({
  item,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  item: FeedItem;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: (item: FeedItem) => void;
  t: TFunction;
}) {
  if (isMaintenance(item)) {
    return (
      <MaintenanceRow
        alert={item}
        plate={plate}
        locale={locale}
        timeZone={timeZone}
        selected={selected}
        onSelect={onSelect}
        t={t}
      />
    );
  }
  if (isTrackerHealth(item)) {
    // No `onSelect`/`selected`: it is a Link to the vehicle, not a map-select
    // button. See TrackerHealthRow for why calling onSelect here would be wrong.
    return <TrackerHealthRow alert={item} plate={plate} locale={locale} timeZone={timeZone} t={t} />;
  }
  if (isPower(item)) {
    // PowerRow decides internally whether it is a select button (a power_cut with a
    // last-known fix, which centres the map on onSelect) or a Link to the vehicle
    // (a restoration, or a cut with no fix). It takes onSelect/selected either way.
    return (
      <PowerRow
        alert={item}
        plate={plate}
        locale={locale}
        timeZone={timeZone}
        selected={selected}
        onSelect={onSelect}
        t={t}
      />
    );
  }
  if (isIgnition(item)) {
    // 🔴 SLICE 56b — IgnitionRow now decides internally whether it is a select
    // button (an ignition event WITH a last-known fix, which centres the map on
    // onSelect) or a Link to the vehicle (no fix). It takes onSelect/selected either
    // way — quiet info tone throughout, mirroring PowerRow.
    return (
      <IgnitionRow
        alert={item}
        plate={plate}
        locale={locale}
        timeZone={timeZone}
        selected={selected}
        onSelect={onSelect}
        t={t}
      />
    );
  }
  if (isTamper(item)) {
    // Like PowerRow, TamperRow decides internally whether it is a select button (a
    // tamper WITH a last-known fix, which centres the map on onSelect) or a Link to
    // the vehicle (no fix). It takes onSelect/selected either way.
    return (
      <TamperRow
        alert={item}
        plate={plate}
        locale={locale}
        timeZone={timeZone}
        selected={selected}
        onSelect={onSelect}
        t={t}
      />
    );
  }
  if (isIdle(item)) {
    // 🔴 SLICE 66k — IdleRow decides internally whether it is a select button (an
    // idle event WITH a fix, which centres the map on onSelect) or a Link to the
    // vehicle (no fix). It takes onSelect/selected either way — quiet info tone
    // throughout, mirroring IgnitionRow.
    return (
      <IdleRow
        alert={item}
        plate={plate}
        locale={locale}
        timeZone={timeZone}
        selected={selected}
        onSelect={onSelect}
        t={t}
      />
    );
  }
  if (isDataDelayed(item)) {
    // 🔴 SLICE 76 — like TrackerHealthRow, no `onSelect`/`selected`: it is a Link
    // to the vehicle, not a map-select button. A stale point stream has no
    // trustworthy location, so it must never be handed to the map.
    return <DataDelayedRow alert={item} plate={plate} locale={locale} timeZone={timeZone} t={t} />;
  }
  if (isOverspeed(item)) {
    return (
      <OverspeedRow
        alert={item}
        plate={plate}
        locale={locale}
        timeZone={timeZone}
        selected={selected}
        onSelect={onSelect}
        t={t}
      />
    );
  }
  if (isAfterHours(item)) {
    return (
      <AfterHoursRow
        alert={item}
        plate={plate}
        locale={locale}
        timeZone={timeZone}
        selected={selected}
        onSelect={onSelect}
        t={t}
      />
    );
  }
  if (isMovedWhileParked(item)) {
    // 🔴 SLICE 82a — a POSITIONED item (observed fix always present), so like
    // OverspeedRow/AfterHoursRow it is always a select button that centres the map.
    return (
      <MovedWhileParkedRow
        alert={item}
        plate={plate}
        locale={locale}
        timeZone={timeZone}
        selected={selected}
        onSelect={onSelect}
        t={t}
      />
    );
  }
  if (isDriverUnlink(item)) {
    // 🔴 SLICE D2 — now a TYPED member of the feed union (api `DriverUnlinkAlertZ`).
    // A request is not a positioned event (no observed fix), so it is a plain
    // markable row, never a map-select — `DriverUnlinkRow` reads base fields only.
    // The onSelect marks it read; it carries no location for the map to centre on.
    return (
      <DriverUnlinkRow
        item={item}
        plate={plate}
        locale={locale}
        timeZone={timeZone}
        selected={selected}
        onSelect={() => onSelect(item)}
        t={t}
      />
    );
  }
  if (isGeofenceCrossing(item)) {
    return (
      <AlertRow
        alert={item}
        plate={plate}
        locale={locale}
        timeZone={timeZone}
        selected={selected}
        onSelect={onSelect}
        t={t}
      />
    );
  }
  // Compile-time exhaustiveness (item is `never` here); at runtime, a type this
  // build does not know renders generically instead of throwing (§F).
  assertAllFeedItemsHandled(item);
  const unknownItem = item as unknown as GenericWireAlert;
  return (
    <GenericAlertRow
      item={unknownItem}
      plate={plate}
      locale={locale}
      timeZone={timeZone}
      selected={selected}
      onSelect={() => onSelect(item as unknown as FeedItem)}
      t={t}
    />
  );
}
